import { useQuery, QueryKey } from "@tanstack/react-query";
import { Skeleton, Typography } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import { CommonGraphSummaryResponse } from "@/pages/query/types";
import { cn } from "@/lib/utils";
import { formatCost } from "@/helpers/utils";

export interface TotalCostHeaderProps<TParams> {
  /**
   * Unique query key for React Query caching
   */
  queryKey: QueryKey;
  /**
   * Function that fetches the graph summary data
   */
  queryFn: (params: TParams) => Promise<CommonGraphSummaryResponse | undefined>;
  /**
   * Parameters to pass to the query function
   */
  params: TParams;
  /**
   * Custom title to display before the cost value
   * @default "Total Cost"
   */
  title?: string;
  /**
   * Additional CSS classes for the text
   */
  className?: string;
  /**
   * Additional CSS classes for the loader
   */
  loaderClassName?: string;
}

export const TotalCostHeader = <TParams,>({
  queryKey,
  queryFn,
  params,
  title = "Total Cost",
  className,
  loaderClassName
}: TotalCostHeaderProps<TParams>) => {
  const { currency } = useAppState();

  const { data: summary, isLoading } = useQuery({
    queryKey,
    queryFn: () => queryFn(params),
  });

  if (isLoading) {
    return <Skeleton className={cn("al-h-10 al-w-[512px]", loaderClassName)} />;
  }

  return (
    <Typography
      variant={"h4"}
      className={cn("al-p-2 al-rounded al-bg-bg-brand", className)}
    >
      {title} (for the selected time period):{" "}
      <span className={"al-text-brand"}>
        {summary?.total_cost ? formatCost(summary.total_cost, currency) : "NA"}
      </span>
    </Typography>
  );
};
